import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { db, doc, getDoc } from '../firebase';

const TeamContext = createContext({
  teamId: null,
  teamData: null,
  slug: null,
  loading: true,
  error: null,
  locked: false,
  slugMeta: null,
});

const getSlugFromPath = (pathname) => {
  const match = pathname.match(/^\/t\/([a-z0-9-]+)/);
  return match ? match[1] : null;
};

export const TeamProvider = ({ children }) => {
  const location = useLocation();
  const slug = getSlugFromPath(location.pathname);

  const [teamId, setTeamId] = useState(null);
  const [teamData, setTeamData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [locked, setLocked] = useState(false);
  const [slugMeta, setSlugMeta] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const reset = () => {
      setError(null);
      setTeamId(null);
      setTeamData(null);
      setLocked(false);
      setSlugMeta(null);
    };

    const fetchTeam = async () => {
      setLoading(true);
      reset();

      if (!slug) {
        setLoading(false);
        return;
      }

      try {
        const slugSnap = await getDoc(doc(db, 'slugs', slug));
        if (cancelled) return;

        if (!slugSnap.exists()) {
          setError(`Tim "${slug}" ne postoji.`);
          setLoading(false);
          return;
        }

        const slugData = slugSnap.data();
        setSlugMeta(slugData);

        const resolvedTeamId = slugData?.teamId;
        if (!resolvedTeamId) {
          setError(`Slug "${slug}" je oštećen (nema teamId).`);
          setLoading(false);
          return;
        }

        const teamSnap = await getDoc(doc(db, 'teams', resolvedTeamId));
        if (cancelled) return;

        if (!teamSnap.exists()) {
          setError(`Tim "${slug}" ne postoji.`);
          setLoading(false);
          return;
        }

        const resolvedTeamData = { id: teamSnap.id, ...teamSnap.data() };
        const isUnlocked = localStorage.getItem(`team-unlocked:${slug}`) === 'true';

        setTeamId(teamSnap.id);
        setTeamData(resolvedTeamData);
        setLocked(Boolean((resolvedTeamData.passwordEnabled || resolvedTeamData.visibility === 'private') && !isUnlocked));
      } catch (e) {
        console.error('Greška pri dohvaćanju tima:', e);
        if (!cancelled) {
          if (e?.code === 'permission-denied') {
            setError(`Nemate pristup timu "${slug}".`);
          } else {
            setError(e.message || 'Greška pri dohvaćanju tima.');
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchTeam();

    return () => {
      cancelled = true;
    };
  }, [slug]);

  return (
    <TeamContext.Provider
      value={{ teamId, teamData, slug, loading, error, locked, slugMeta }}
    >
      {children}
    </TeamContext.Provider>
  );
};

export const useTeam = () => useContext(TeamContext);

export default TeamContext;
